import {
	QueryClient,
	useMutation,
	useQueryClient,
} from "@tanstack/react-query";

import { MILL_2, MillId } from "@/setup/mill-config/mill-config";
import {
	updateHeatMechanicalResult,
	upsertMechanicalTestRunDisposition,
} from "../../repositories/CampaignRepository";
import { PLACEHOLDER_RUN_ID } from "./runGroups";
import { MechanicalRunEdit } from "./types";

type Params = {
	heatId?: number | null;
	millId?: MillId;
	onError: (message: string) => void;
};

export const invalidateHeatQueries = (
	queryClient: QueryClient,
	{ heatId, millId }: { heatId?: number | null; millId: MillId },
) =>
	Promise.all(
		[
			["heatMechanicalResults", heatId],
			["heatTestDispositions", millId, heatId],
			["mechanicalResultVersions"],
			["chemistries-mill-heats"],
		].map((queryKey) => queryClient.invalidateQueries({ queryKey })),
	);

export const useSaveMechanicalRun = ({
	heatId,
	millId = MILL_2.id,
	onError,
}: Params) => {
	const queryClient = useQueryClient();

	const invalidateHeat = () =>
		invalidateHeatQueries(queryClient, { heatId, millId });

	const valuesMutation = useMutation(updateHeatMechanicalResult, {
		onSuccess: invalidateHeat,
		onError: (e: Error) => onError(e.message),
	});

	const dispositionMutation = useMutation(upsertMechanicalTestRunDisposition, {
		onSuccess: invalidateHeat,
		onError: (e: Error) => onError(e.message),
	});

	const saveRun = async (runId: number, edit: MechanicalRunEdit) => {
		if (edit.results.length > 0)
			await valuesMutation.mutateAsync({
				mechanicalTestRunId: runId,
				results: edit.results.map(({ result, values }) => ({
					HeatMechanicalResultId: result.HeatMechanicalResultId,
					SampleSequence: result.SampleSequence,
					YieldValue: values.yieldValue,
					TensileValue: values.tensileValue,
					ElongationValue: values.elongationValue,
					PercentRAValue: values.percentRAValue,
				})),
			});

		if (edit.disposition.changed) {
			const isPlaceholder = runId === PLACEHOLDER_RUN_ID;

			await dispositionMutation.mutateAsync({
				mechanicalTestRunDispositionId:
					edit.disposition.dispositionId ?? undefined,
				mechanicalTestRunId: isPlaceholder ? undefined : runId,
				heatId: isPlaceholder ? (heatId ?? undefined) : undefined,
				value: edit.disposition.value,
				comment: edit.disposition.comment,
			});
		}
	};

	return {
		saveRun,
		isSaving: valuesMutation.isPending || dispositionMutation.isPending,
	};
};
